import { fetchWrapper } from './fetch/fetchWrapper';
import { ENVIRONMENT_URL } from '../settings';

export const ALLOWED_FILE_TYPES = {
  'image/jpeg': 'image',
  'image/png': 'image',
  'video/mp4': 'video',
  'video/quicktime': 'video',
  'application/pdf': 'pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
  'text/plain': 'txt',
} as const;

export const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

const fileTypeLookup: Record<string, string | undefined> = ALLOWED_FILE_TYPES;

export const isImageMime = (mime: string): boolean => fileTypeLookup[mime] === 'image';

type UploadFileParams = Readonly<{
  file: File;
}>;

type MediaUploadResponse = Readonly<{
  upload_url: string;
  id: number;
  uri: string;
  s3_key: string;
}>;

export const isValidFileType = (file: File): boolean => {
  return Object.keys(ALLOWED_FILE_TYPES).includes(file.type);
};

export const uploadFile = async ({ file }: UploadFileParams) => {
  const mediaPayload = {
    type: ALLOWED_FILE_TYPES[file.type as keyof typeof ALLOWED_FILE_TYPES] || 'pdf',
    name: file.name,
    size: file.size.toString(),
  };

  const uploadUrlResponse = await fetchWrapper(`${ENVIRONMENT_URL}/media`, {
    method: 'POST',
    body: JSON.stringify(mediaPayload),
  });

  if (!uploadUrlResponse.ok) {
    throw new Error('Failed to get upload URL');
  }

  const {
    upload_url: uploadUrl,
    id,
    uri,
    s3_key,
  } = (await uploadUrlResponse.json()) as Partial<MediaUploadResponse>;

  if (!uploadUrl || id === undefined || !uri || !s3_key) {
    throw new Error('Media upload response missing required fields');
  }

  // Step 2: Upload the file to the pre-signed URL
  // Note: This is a direct upload to S3, not our API, so we use native fetch
  const uploadResponse = await fetch(uploadUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': file.type,
      'Content-Length': file.size.toString(),
    },
    body: file,
  });

  if (!uploadResponse.ok) {
    throw new Error('Failed to upload file');
  }

  return {
    mediaId: id,
    newDocumentUri: uri,
    s3Key: s3_key,
  };
};
